import * as React from 'react';

import { cn } from '../../lib/utils';
import type { ToastNotification } from '../../types/toast-types';
import { Toast, ToastClose, ToastTitle } from '../ui/toast';

import { ToastIcon } from './toast-icon';

type ToastCardProps = {
  notification: ToastNotification;
  onRemove: (id: string) => void;
};

// The inline colour wins over the title's `text-gray-*` classes.
const titleStyle: React.CSSProperties = {
  color: 'var(--gray-800, #1F2937)',
};

/** One toast. Each control runs its own handler first, then dismisses the toast. */
export const ToastCard = ({ notification: n, onRemove }: ToastCardProps) => {
  const dismiss = () => onRemove(n.id);
  // High-visibility success: solid green surface, white bold copy, large check mark.
  const isProminent = n.type === 'prominent-success';

  return (
    <Toast
      data-testid="toast-card"
      className={cn(
        'pointer-events-auto w-[28.75rem] flex items-center rounded-md shadow-action-bar',
        isProminent
          ? 'gap-4 p-4 pr-10 border-0 bg-green-600 text-white'
          : 'gap-3 p-3 bg-background border border-border',
      )}
    >
      <ToastIcon type={n.type} />
      <div className="flex-1">
        {n.title && (
          <ToastTitle
            className={
              isProminent
                ? 'font-bold text-text-md text-white'
                : 'font-medium text-sm text-gray-900 dark:text-gray-100'
            }
            style={isProminent ? undefined : titleStyle}
          >
            {n.title}
          </ToastTitle>
        )}
        {n.message && (
          <div
            className={cn(
              'font-bold leading-5 font-dm-sans',
              isProminent
                ? 'text-text-md text-white'
                : 'text-gray-600 dark:text-gray-900 text-sm',
              n.onMessageClick && 'cursor-pointer hover:underline',
              n.messageClassName,
            )}
            onClick={() => {
              n.onMessageClick?.();
              dismiss();
            }}
          >
            {n.message}
          </div>
        )}
      </div>
      {n.action && (
        <button
          onClick={() => {
            n.action?.onClick();
            dismiss();
          }}
          className="px-3 py-1 bg-gray-100 text-gray-600 text-sm leading-5 font-medium font-['DM_Sans'] rounded-md hover:bg-gray-200 transition-colors"
        >
          {n.action.label}
        </button>
      )}
      <div className="ml-auto">
        <ToastClose
          onClick={dismiss}
          className={isProminent ? 'text-white hover:text-white/80' : undefined}
        />
      </div>
    </Toast>
  );
};
